import { useCallback, useEffect, useState } from "react";
import { z as zod } from "zod";

export type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  sources?: { title: string; url: string }[];
};

interface ChatResponse {
  answer?: string;
  message?: string;
}

const UserRoles = ["developer", "designer", "general"] as const;
export type UserRole = (typeof UserRoles)[number];

const userRoleSchema = zod.enum(UserRoles);
const booleanSchema = zod.boolean();
const chatInputSchema = zod.string().trim().min(1, "Please enter a valid question.").max(2000);

const getStoredValue = <T>(key: string, defaultValue: T, schema?: zod.ZodSchema<T>): T => {
  if (typeof window === "undefined") return defaultValue;

  const saved = window.localStorage.getItem(key);
  if (saved === null) return defaultValue;

  try {
    const parsed = JSON.parse(saved);
    if (schema) {
      const result = schema.safeParse(parsed);
      return result.success ? result.data : defaultValue;
    }

    return parsed;
  } catch {
    return defaultValue;
  }
};

const setStoredValue = <T>(key: string, value: T): void => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
};

export const useChat = () => {
  const [userRole, setUserRoleState] = useState<UserRole>("general");
  const [isOpen, setIsOpenState] = useState<boolean>(false);

  useEffect(() => {
    setUserRoleState(getStoredValue("chatUserRole", "general", userRoleSchema));
    setIsOpenState(getStoredValue("chatOpen", false, booleanSchema));
  }, []);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const setUserRole = useCallback((role: UserRole) => {
    setUserRoleState(role);
    setStoredValue("chatUserRole", role);
  }, []);

  const toggleChat = useCallback(() => {
    const newIsOpen = !isOpen;
    setIsOpenState(newIsOpen);
    setStoredValue("chatOpen", newIsOpen);
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (queryText: string) => {
      setLoading(true);
      setError("");

      try {
        const validatedQuery = chatInputSchema.parse(queryText);
        const userMessage: ChatMessage = {
          role: "user",
          content: validatedQuery,
        };

        const newMessageHistory = [...messages, userMessage];
        setMessages(newMessageHistory);

        const apiResponse = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: newMessageHistory,
            userRole,
            currentPage: window.location.pathname,
          }),
        });

        if (!apiResponse.ok) {
          throw new Error(`HTTP ${apiResponse.status}`);
        }

        const response: ChatResponse = await apiResponse.json();
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.answer || response.message || "No response received.",
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Something went wrong. Please try again.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [messages, userRole]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError("");
  }, []);

  return {
    isOpen,
    userRole,
    messages,
    loading,
    error,
    setUserRole,
    toggleChat,
    onSubmit: handleSubmit,
    clearMessages,
  };
};
